import { useLinkedPatients, type LinkedPatient } from '@/services/linkedPatients'
import { useCurrentMedication } from '@/hooks/useCurrentMedication'
import { getPatientAgeText } from '@/lib/utils'
import { isRoleAllowedToEditPatientData } from '@/lib/permissions'
import type { MedRequest } from '@/types/data'
import { useMemo, useState, useRef, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import http from '@/lib/http'
import { useMutation } from '@tanstack/react-query'
import { queryClient } from '@/services/queryClient'
import { useToast } from '@/hooks/use-toast'
import { PatientRedFlag, PencilIcon } from './patient-data'

// -- Types & constants --------------------------------------------------------

/** Medical history field keys in the order they appear in the design. */
const MEDICAL_HISTORY_FIELDS = [
  'allergies',
  'conditions',
  'medications',
  'socialHistory',
  'familyHistory',
  'other'
] as const

type MedicalHistoryField = (typeof MEDICAL_HISTORY_FIELDS)[number]

const FIELD_LABELS: Record<MedicalHistoryField, string> = {
  allergies: 'Allergies',
  conditions: 'Conditions',
  medications: 'Medications',
  socialHistory: 'Social history',
  familyHistory: 'Family history',
  other: 'Other'
}

/**
 * Maps the LinkedPatient snake_case fields from the API to the camelCase
 * keys used in the UI form.
 */
function linkedPatientToMedicalHistory(lp: LinkedPatient): Record<MedicalHistoryField, string> {
  return {
    allergies: lp.allergies ?? '',
    conditions: lp.conditions ?? '',
    medications: lp.medications ?? '',
    socialHistory: lp.social_history ?? '',
    familyHistory: lp.family_history ?? '',
    other: lp.other_observations ?? ''
  }
}

function currentPatientToMedicalHistory(p: MedRequest['patient']): Record<MedicalHistoryField, string> {
  return {
    allergies: p.allergies ?? '',
    conditions: p.conditions ?? '',
    medications: p.medications ?? '',
    socialHistory: p.socialHistory ?? '',
    familyHistory: p.familyHistory ?? '',
    other: p.otherObservations ?? ''
  }
}

// -- Validation ---------------------------------------------------------------

const patientFieldsSchema = z.object({
  allergies: z.string().max(2000, { message: 'Must be less than 2000 characters' }).optional(),
  conditions: z.string().max(2000, { message: 'Must be less than 2000 characters' }).optional(),
  medications: z.string().max(2000, { message: 'Must be less than 2000 characters' }).optional(),
  socialHistory: z.string().max(2000, { message: 'Must be less than 2000 characters' }).optional(),
  familyHistory: z.string().max(2000, { message: 'Must be less than 2000 characters' }).optional(),
  other: z.string().max(2000, { message: 'Must be less than 2000 characters' }).optional()
})

/**
 * Dynamic schema: `patients` is an array where index 0 is the current patient
 * and subsequent indices are the linked patients.
 */
const buildFormSchema = (patientCount: number) =>
  z.object({
    patients: z.tuple(
      Array.from({ length: patientCount }, () => patientFieldsSchema) as [
        typeof patientFieldsSchema,
        ...(typeof patientFieldsSchema)[]
      ]
    )
  })

type FormValues = z.infer<ReturnType<typeof buildFormSchema>>

// -- Sub-components -----------------------------------------------------------

export function LinkedBadge({ count }: { count: number }) {
  return (
    <span className='inline-flex items-center gap-1.5 rounded-full border border-custom-neutral-200 bg-white px-3 py-1 text-sm font-medium text-custom-neutral-800'>
      <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M9.33333 4.66667C10.622 4.66667 11.6667 3.622 11.6667 2.33333C11.6667 1.04467 10.622 0 9.33333 0C8.04467 0 7 1.04467 7 2.33333C7 3.622 8.04467 4.66667 9.33333 4.66667Z'
          fill='#6B7280'
        />
        <path
          d='M4.66667 4.66667C5.95533 4.66667 7 3.622 7 2.33333C7 1.04467 5.95533 0 4.66667 0C3.378 0 2.33333 1.04467 2.33333 2.33333C2.33333 3.622 3.378 4.66667 4.66667 4.66667Z'
          fill='#6B7280'
        />
        <path
          d='M4.66667 5.83333C2.74167 5.83333 0 6.79667 0 8.75V9.33333C0 9.975 0.525 10.5 1.16667 10.5H8.16667C8.80833 10.5 9.33333 9.975 9.33333 9.33333V8.75C9.33333 6.79667 6.59167 5.83333 4.66667 5.83333Z'
          fill='#6B7280'
        />
        <path
          d='M9.38 5.845C9.36333 5.845 9.35833 5.83333 9.33333 5.83333C8.89167 5.83333 8.40333 5.93833 7.91 6.10833C8.56833 6.59667 9.33333 7.28 9.33333 8.75V9.33333C9.33333 9.73833 9.21667 10.1317 9.01333 10.5H12.8333C13.475 10.5 14 9.975 14 9.33333V8.75C14 6.92167 11.585 5.93833 9.38 5.845Z'
          fill='#6B7280'
        />
      </svg>
      {count} {count === 1 ? 'patient' : 'patients'} linked
    </span>
  )
}

/** Fixed width for each patient data column (px). */
export const COLUMN_WIDTH = 180

// -- Main component -----------------------------------------------------------

export default function LinkedPatientData() {
  const medRequestQuery = useCurrentMedication(true)
  const medRequest = medRequestQuery?.data
  const patient = medRequest?.patient

  /**
   * `linkedPatientHash` is the shared group identifier (patient.linked_patient_hash).
   * This is NOT a patient.hash — it links multiple patient records together.
   */
  const linkedPatientHash = patient?.linkedPatientHash
  const { data: linkedData, isLoading } = useLinkedPatients(linkedPatientHash)

  /**
   * Build the ordered list of patients to display as columns.
   * First column is always the current patient from the med request ("Patient data").
   * Remaining columns are the other linked patients, excluding the current one.
   */
  const linkedOthers = useMemo(() => {
    if (!linkedData?.patients || !patient) return []
    return linkedData.patients.filter(lp => lp.hash !== patient.id)
  }, [linkedData, patient])

  const linkedCount = linkedOthers.length

  // Don't render the linked view if there are no linked patients
  if (!linkedPatientHash || (!isLoading && linkedCount === 0)) return null
  if (isLoading || !patient || !medRequest) return null

  return (
    <LinkedPatientDataInner
      patient={patient}
      medRequest={medRequest}
      linkedOthers={linkedOthers}
      linkedCount={linkedCount}
    />
  )
}

/**
 * Inner component split out so that the form hook is initialised at a stable
 * point — after we know the exact number of linked patients. The array length
 * will not change between renders because `linkedOthers` is memoised above and
 * the parent guards against zero-length / loading states.
 */
function LinkedPatientDataInner({
  patient,
  medRequest,
  linkedOthers,
  linkedCount
}: {
  patient: MedRequest['patient']
  medRequest: MedRequest
  linkedOthers: LinkedPatient[]
  linkedCount: number
}) {
  const { toast } = useToast()
  const canEdit = isRoleAllowedToEditPatientData()
  const [isEditing, setIsEditing] = useState(false)

  // All patient hashes in column order: [current, ...linked]
  const patientHashes = useMemo(() => [patient.id, ...linkedOthers.map(lp => lp.hash)], [patient.id, linkedOthers])

  const totalColumns = patientHashes.length

  // Build default values: patients[0] = current, patients[1..n] = linked
  const defaultValues = useMemo<FormValues>(
    () => ({
      patients: [currentPatientToMedicalHistory(patient), ...linkedOthers.map(lp => linkedPatientToMedicalHistory(lp))]
    }),
    [patient, linkedOthers]
  )

  const formSchema = useMemo(() => buildFormSchema(totalColumns), [totalColumns])

  const {
    register,
    handleSubmit,
    formState: { isDirty, errors, dirtyFields },
    reset
  } = useForm<FormValues>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(formSchema)
  })

  // Mutation: sends updates for each patient whose fields were changed.
  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['updateLinkedPatients'],
    mutationFn: async (formData: FormValues) => {
      const updates: Promise<unknown>[] = []

      formData.patients.forEach((fields, idx) => {
        const patientDirty = dirtyFields.patients?.[idx]
        if (!patientDirty) return

        const hash = patientHashes[idx]
        const body = {
          family_history: fields.familyHistory,
          other: fields.other,
          social_history: fields.socialHistory,
          allergies: fields.allergies,
          conditions: fields.conditions,
          medications: fields.medications
        }

        updates.push(http.post(`/patients/${hash}/update-details`, body))
      })

      await Promise.all(updates)
      return formData
    },
    onSuccess: formData => {
      // Update the current patient in the med-request cache
      const currentFields = formData.patients[0]
      queryClient.setQueryData(['medicationRequestById', medRequest.id], (data: MedRequest) => ({
        ...data,
        patient: {
          ...data.patient,
          allergies: currentFields.allergies,
          conditions: currentFields.conditions,
          medications: currentFields.medications,
          socialHistory: currentFields.socialHistory,
          familyHistory: currentFields.familyHistory,
          otherObservations: currentFields.other
        }
      }))
      // Refresh linked patients data
      queryClient.invalidateQueries({ queryKey: ['linkedPatients'] })
    },
    onError: error => {
      console.error(error)
      toast({
        title: 'Error updating patient',
        description: 'Please try again',
        variant: 'destructive'
      })
    }
  })

  const onSubmit = async (data: FormValues) => {
    await mutateAsync(data)
    setIsEditing(false)
    reset(data)
  }

  const handleCancel = () => {
    setIsEditing(false)
    reset()
  }

  // Column headers
  const columnHeaders = ['Patient data', ...linkedOthers.map((_, i) => `Linked Patient ${i + 1}`)]

  // Demographics data per column
  const names = [
    patient.firstName ? `${patient.firstName} ${patient.lastName}` : '-',
    ...linkedOthers.map(lp => (lp.first_name ? `${lp.first_name} ${lp.last_name}` : '-'))
  ]
  const ages = [
    getPatientAgeText(patient.dob),
    ...linkedOthers.map(lp => getPatientAgeText(lp.date_of_birth ?? undefined))
  ]
  const genders = [patient.gender || '-', ...linkedOthers.map(lp => lp.gender || '-')]
  const postcodes = [medRequest.orderPostCode || '-', ...linkedOthers.map(lp => lp.postal_code || '-')]

  /** Inline min-width so all patient columns render at their fixed size. */
  const scrollableWidth = totalColumns * COLUMN_WIDTH

  // Synchronise all horizontal scroll containers (demographics + each medical history row).
  // All containers with class 'hist-scroll-row' plus the demographics container stay in sync.
  const demoScrollRef = useRef<HTMLDivElement>(null)
  const syncing = useRef(false)

  const syncAllScrolls = useCallback((sourceEl: HTMLDivElement | null) => {
    if (syncing.current || !sourceEl) return
    syncing.current = true
    const scrollLeft = sourceEl.scrollLeft

    // Sync demographics container
    if (demoScrollRef.current && demoScrollRef.current !== sourceEl) {
      demoScrollRef.current.scrollLeft = scrollLeft
    }

    // Sync all medical history row containers
    const rows = sourceEl.closest('form')?.querySelectorAll<HTMLDivElement>('.hist-scroll-row')
    rows?.forEach(row => {
      if (row !== sourceEl) row.scrollLeft = scrollLeft
    })

    syncing.current = false
  }, [])

  return (
    <div id='patient-data'>
      <div className='flex items-center gap-3 mb-4'>
        <p className='text-custom-neutral-600 text-xl font-semibold leading-[133.4%]'>Patient Data</p>
        <LinkedBadge count={linkedCount} />
      </div>

      <div className='rounded-2xl border border-custom-neutral-700 bg-white py-8'>
        <div className='px-12'>
          <PatientRedFlag patient={patient} />
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* ── Demographics ── */}
          <div className='flex'>
            {/* Sticky left label column */}
            <div className='shrink-0 pl-12 pr-4 flex flex-col w-[160px]'>
              <div className='h-9' />
              {(['Patient name', 'Age', 'Gender', 'Postcode'] as const).map(label => (
                <div key={label} className='py-2 flex items-center h-[44px]'>
                  <span className='text-sm font-medium text-custom-neutral-800 whitespace-nowrap'>{label}</span>
                </div>
              ))}
            </div>

            {/* Scrollable data columns */}
            <div
              ref={demoScrollRef}
              onScroll={() => syncAllScrolls(demoScrollRef.current)}
              className='overflow-x-auto pr-12 min-w-0'
            >
              <div style={{ minWidth: scrollableWidth }} className='flex'>
                {columnHeaders.map((header, colIdx) => (
                  <div key={colIdx} style={{ width: COLUMN_WIDTH, minWidth: COLUMN_WIDTH }} className='px-2 shrink-0'>
                    <div className='py-2 h-9 flex items-center'>
                      <span className='text-sm font-bold text-[#1D1D1D] whitespace-nowrap'>{header}</span>
                    </div>
                    {[names, ages, genders, postcodes].map((rowValues, rowIdx) => (
                      <div key={rowIdx} className='py-2 h-[44px]'>
                        <div className='rounded-lg border border-custom-neutral-200 px-3 py-2 text-sm text-custom-neutral-800 truncate'>
                          {rowValues[colIdx] || '-'}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Full-width divider ── */}
          <div className='mx-12 my-6 border-t border-custom-neutral-200' />

          {/* ── Medical history header (always visible, outside scroll) ── */}
          <div className='flex items-center justify-between px-12 mb-4'>
            <p className='text-[#1D1D1D] text-[18px] font-semibold leading-[133.4%]'>Medical history</p>
            {isEditing ? (
              <div className='flex gap-6 items-center'>
                <button
                  type='submit'
                  className={`flex h-10 items-center justify-center rounded-2xl bg-[#153CB7] px-5 hover:bg-[#0F2E8A] transition-colors duration-200 ${
                    !isDirty || isPending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  }`}
                  disabled={!isDirty || isPending}
                >
                  <span className='text-white text-sm leading-[170%] font-semibold whitespace-nowrap'>
                    {isPending ? 'Saving...' : 'Save information'}
                  </span>
                </button>
                <button
                  type='button'
                  disabled={isPending}
                  onClick={handleCancel}
                  className='text-primary text-sm font-medium leading-[170%] whitespace-nowrap'
                >
                  Cancel
                </button>
              </div>
            ) : (
              canEdit && (
                <button
                  type='button'
                  onClick={() => setIsEditing(true)}
                  className='text-primary text-sm font-medium leading-[170%] inline-flex items-center gap-2 whitespace-nowrap'
                >
                  Edit information <PencilIcon />
                </button>
              )
            )}
          </div>

          {/* ── Medical history cells — row-based so label + cells share height ── */}
          {MEDICAL_HISTORY_FIELDS.map((field, fieldIdx) => {
            const isLastRow = fieldIdx === MEDICAL_HISTORY_FIELDS.length - 1

            return (
              <div key={field} className='flex py-2'>
                {/* Sticky row label */}
                <div className='shrink-0 pl-12 pr-4 flex items-start pt-2 w-[160px]'>
                  <span className='text-sm font-bold text-[#1D1D1D] whitespace-nowrap'>{FIELD_LABELS[field]}</span>
                </div>

                {/* Scrollable cells — only the last row shows a scrollbar */}
                <div
                  onScroll={e => syncAllScrolls(e.currentTarget)}
                  className='overflow-x-auto pr-12 min-w-0 hist-scroll-row'
                  style={isLastRow ? undefined : { scrollbarWidth: 'none' }}
                >
                  <div style={{ minWidth: scrollableWidth }} className='flex'>
                    {patientHashes.map((_, colIdx) => {
                      const fieldPath = `patients.${colIdx}.${field}` as const
                      const fieldErrors = errors.patients?.[colIdx]?.[field]

                      return (
                        <div
                          key={colIdx}
                          style={{ width: COLUMN_WIDTH, minWidth: COLUMN_WIDTH }}
                          className='px-2 shrink-0'
                        >
                          {isEditing ? (
                            <div>
                              <textarea
                                className={`text-sm leading-6 border ${
                                  fieldErrors ? 'border-red-500' : 'border-custom-neutral-200'
                                } rounded-2xl pt-3 px-3 w-full h-[104px] resize-none`}
                                {...register(fieldPath)}
                              />
                              {fieldErrors && (
                                <p className='text-red-500 text-sm mt-1'>
                                  {fieldErrors.message?.toString() || 'This field has an error'}
                                </p>
                              )}
                            </div>
                          ) : (
                            <div className='rounded-lg border border-custom-neutral-200 px-3 py-2 text-sm text-custom-neutral-800 whitespace-pre-wrap min-h-[28px]'>
                              {defaultValues.patients[colIdx][field] || '-'}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )
          })}
        </form>
      </div>
    </div>
  )
}
